import { DestroyRef, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

/**
 * Tracks open form dialogs so browser back/forward navigation can warn about their unsaved changes.
 *
 * Dialogs register through `protectUnsavedChanges()` rather than calling `register` directly; the
 * entry is removed when the dialog is destroyed. See `unsavedDialogsGuard` for the navigation side.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedDialogsService {
  private readonly dialogs = new Map<MatDialogRef<unknown>, () => boolean>();

  register(dialogRef: MatDialogRef<unknown>, isDirty: () => boolean, destroyRef: DestroyRef): void {
    this.dialogs.set(dialogRef, isDirty);
    destroyRef.onDestroy(() => this.dialogs.delete(dialogRef));
  }

  isRegistered(dialogRef: MatDialogRef<unknown>): boolean {
    return this.dialogs.has(dialogRef);
  }

  hasDirtyDialogs(): boolean {
    return [...this.dialogs.values()].some((isDirty) => isDirty());
  }

  /** Closes every registered dialog without asking; the caller has already confirmed the discard. */
  discardAll(): void {
    const refs = [...this.dialogs.keys()];
    // Cleared up front because the dialogs are only destroyed once their exit animation ends.
    this.dialogs.clear();
    refs.forEach((ref) => ref.close());
  }
}
